import type { TranslateFunc } from '../context/cms-context'
import { formatBetLineLabel } from './bet-line-helpers'

// ported from plc-app src/lib/helpers/get-bet-item-title.ts so line titles read the same as in the app

type GameWithScoreNoun = { systemId?: string | null, scoreNoun?: string | null }

const addCommasToNumber = (value: number): string => value.toLocaleString('en-US')

// i18next hands back the key when a phrase is missing, fall back to a readable label instead
const translateOrFallback = (
  translate: TranslateFunc,
  phrase: string,
  options: Record<string, unknown>,
  fallback: string,
): string => {
  const result = translate(phrase, options)
  return !result || result === phrase ? fallback : result
}

export const getBetItemTitle = ({
  title,
  value,
  translate,
  game,
  games,
}: {
  title: string | null | undefined
  value: string | null | undefined
  translate: TranslateFunc
  game: string | null | undefined
  games: GameWithScoreNoun[]
}): string => {
  const sanitisedLineTitle = title?.toLowerCase() || ''
  const fallback = [formatBetLineLabel(title), value].filter(Boolean).join(' ')

  const valueAsNumber = parseInt(value || '0', 10)
  const valueFormatted = addCommasToNumber(valueAsNumber)

  if (sanitisedLineTitle === 'spread') {
    return translateOrFallback(
      translate,
      `bets-spread-${valueAsNumber > 0 ? 'positive' : 'negative'}-description_micro`,
      { value: valueAsNumber },
      fallback,
    )
  }

  const pluralSuffix =
    sanitisedLineTitle === 'main' || sanitisedLineTitle === 'kills'
      ? valueAsNumber > 1 ? '_other' : '_one'
      : ''
  const phrase = `bets-${sanitisedLineTitle}-description_micro${pluralSuffix}`

  if (sanitisedLineTitle === 'opponent_score') {
    const scoreNoun = games.find(cmsGame => cmsGame.systemId === game)?.scoreNoun || 'point'
    return translateOrFallback(translate, phrase, {
      value: valueFormatted,
      game_score_noun: translate(`bet_title_${scoreNoun}${valueAsNumber > 1 ? '_plural' : ''}`),
    }, fallback)
  }

  if (sanitisedLineTitle === 'death') {
    if (value === '1') return translateOrFallback(translate, `${phrase}_singular`, { value }, fallback)
    if (value === '0' || !value) return translateOrFallback(translate, `${phrase}_zero`, { value }, fallback)
  }

  if (sanitisedLineTitle === 'player_grade') {
    return translateOrFallback(translate, phrase, { value }, fallback)
  }

  return translateOrFallback(translate, phrase, { value: valueFormatted }, fallback)
}

export const getBountyTitle = ({
  translate,
  title,
  bountyIncrement,
}: {
  translate: TranslateFunc
  title: string | null | undefined
  bountyIncrement: string
}): string => {
  const sanitisedLineTitle = title?.toLowerCase()
  const fallback = `${formatBetLineLabel(title)} Bounty`

  return Number(bountyIncrement) === 1
    ? translateOrFallback(translate, `bets-bounty-${sanitisedLineTitle}-description_micro_one`, {}, fallback)
    : translateOrFallback(translate, `bets-bounty-${sanitisedLineTitle}-description_micro_other`, { frequency: bountyIncrement }, fallback)
}
